import {
  Form, FormField, FormHandlers,
  ICON_PERCENT,
  Message,
  SelectOption,
  YesOrNotButtons,
} from '@doveit/bricks';
import React from 'react';
import { formatEuro } from '@doveit/hammer';
import { InvoiceStatus, InvoiceType, PaymentMethod } from '../../../domain/types';
import { invoiceFormValidationSchema } from './InvoiceForm.schema';
import { asSelectOptions } from '../../../select-options/utils/asSelectOptions';
import { paymentMethodWizardLabels } from '../../../labels';
import { computeGrossValue } from '../../utils';

export interface InvoiceFormModel {
  type: InvoiceType | '',
  value: string,
  paidValue: string,
  vat: string,
  paymentMethod: PaymentMethod | '',
  paymentDate: string,
  invoiceId: string,
  proformaInvoiceId: string,
  notes: string,
}

export interface InvoiceFormProps extends React.AriaAttributes {
  typeOptions: SelectOption[],
  invoiceStatus?: InvoiceStatus,
  hiddenFields?: {
    invoiceId?: boolean,
    proformaInvoiceId?: boolean,
    vat?: boolean,
    paidValue?: boolean,
    paymentMethod?: boolean,
    notes?: boolean,
  },
  disabledFields?: Partial<Record<keyof InvoiceFormModel, boolean>>,
  paymentDateLabel?: string,
  disabledFieldsInfoMessage?: string,
  initialValues?: Partial<InvoiceFormModel>,
  onSubmit: (values: InvoiceFormModel) => void,
  loading?: boolean,
  disabled?: boolean,
  innerRef?: React.MutableRefObject<FormHandlers>,
}

const INVOICE_FORM_DEFAULT_VALUES: InvoiceFormModel = {
  type: '',
  value: '',
  paidValue: '',
  vat: '',
  paymentMethod: '',
  paymentDate: '',
  invoiceId: '',
  proformaInvoiceId: '',
  notes: '',
};

const InvoiceForm: React.FC<InvoiceFormProps> = ({
  typeOptions = [],
  invoiceStatus,
  hiddenFields,
  disabledFields,
  disabledFieldsInfoMessage,
  paymentDateLabel = 'Data presunta',
  loading = false,
  disabled = false,
  onSubmit,
  initialValues = {},
  innerRef,
  ...rest
}) => {
  const mergedInitialValues = React.useMemo(() => ({
    ...INVOICE_FORM_DEFAULT_VALUES,
    ...initialValues,
  }), [initialValues]);

  const [isValueTotallyPaid, setIsValueTotallyPaid] = React.useState(!mergedInitialValues.paidValue);

  return (
    <Form
      enableReinitialize
      onSubmit={onSubmit}
      validationSchema={invoiceFormValidationSchema(hiddenFields, isValueTotallyPaid, invoiceStatus)}
      initialValues={mergedInitialValues}
      loading={loading}
      disabled={disabled}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, setFieldValue }) => {
        const valueNum = parseFloat(values.value) || 0;
        const vatNum = parseFloat(values.vat) || undefined;

        const grossValue = computeGrossValue(valueNum, vatNum);

        return (
          <Form.Fieldset legend="Dati pagamento">
            {disabledFieldsInfoMessage && (
              <Form.Item>
                <Message type="warning" message={disabledFieldsInfoMessage} />
              </Form.Item>
            )}
            {typeOptions.length > 0 && (
              <Form.Item size={{ MD: 3 / 6 }}>
                <Form.Select
                  name="type"
                  aria-label="Campo per selezionare la tipologia del pagamento"
                  label="Tipologia"
                  options={typeOptions}
                  disabled={disabledFields?.type}
                  required
                />
              </Form.Item>
            )}
            <Form.Item size={{ MD: 2 / 6 }}>
              <Form.DecimalPriceField
                name="value"
                aria-label="Campo per specificare l'importo netto del pagamento"
                label="Importo netto"
                placeholder="Es. 2000,50"
                fixedDecimalScale
                disabled={disabledFields?.value}
                help={grossValue ? `Importo lordo: ${formatEuro(grossValue)}` : undefined}
                required
              />
            </Form.Item>

            {!hiddenFields?.vat && (
              <Form.Item size={{ MD: 1 / 6 }}>
                <Form.DecimalPriceField
                  name="vat"
                  aria-label="Campo per specificare l'IVA del pagamento"
                  label="IVA"
                  placeholder="Es. 22"
                  disabled={disabledFields?.vat}
                  icon={ICON_PERCENT}
                  required
                />
              </Form.Item>
            )}

            {!hiddenFields?.paidValue && (
              <>
                <Form.Item size={{ MD: 1 / 2 }}>
                  <FormField
                    label="È stato pagato l'intero importo?"
                  >
                    <YesOrNotButtons
                      value={isValueTotallyPaid ? 'true' : 'false'}
                      aria-label="Campo per specificare se il pagamento è stato pagato interamente"
                      disabled={disabledFields?.paidValue}
                      onChange={(event) => {
                        const isTotallyPaid = event.target.value === 'true';
                        if (isTotallyPaid) {
                          setFieldValue('paidValue', '');
                        }
                        setIsValueTotallyPaid(isTotallyPaid);
                      }}
                    />
                  </FormField>
                </Form.Item>
                <Form.Item size={{ MD: 1 / 3 }}>
                  <Form.DecimalPriceField
                    name="paidValue"
                    aria-label="Campo per specificare l'importo netto effettivo del pagamento"
                    label="Importo netto effettivo"
                    placeholder="Es. 2000,50"
                    fixedDecimalScale
                    disabled={disabledFields?.paidValue || isValueTotallyPaid}
                    required
                  />
                </Form.Item>
              </>
            )}

            <Form.Item size={{ MD: 1 / 2 }}>
              <Form.Input
                name="paymentDate"
                label={paymentDateLabel}
                type="date"
                aria-label="Campo per specificare la data del pagamento"
                disabled={disabledFields?.paymentDate}
                required
              />
            </Form.Item>

            {!hiddenFields?.proformaInvoiceId && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="proformaInvoiceId"
                  label="Rif. Proforma"
                  aria-label="Campo per specificare il riferimento del proforma"
                  placeholder="Es. PRO-XX-XXXX"
                  disabled={disabledFields?.proformaInvoiceId}
                  required={invoiceStatus === InvoiceStatus.PROFORMA}
                />
              </Form.Item>
            )}

            {!hiddenFields?.invoiceId && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.Input
                  name="invoiceId"
                  label="Rif. Fattura"
                  aria-label="Campo per specificare il riferimento della fattura"
                  placeholder="Es. FAT-XX-XXXX"
                  disabled={disabledFields?.invoiceId}
                  required
                />
              </Form.Item>
            )}

            {!hiddenFields?.paymentMethod && (
              <Form.Item size={{ MD: 1 / 2 }}>
                <Form.RadioButtonGroup
                  name="paymentMethod"
                  label="Tramite"
                  aria-label="Campo per specificare il metodo di pagamento"
                  itemSize={{ MD: 1 / 2 }}
                  items={asSelectOptions(paymentMethodWizardLabels, false)}
                  required
                  disabled={disabledFields?.paymentMethod}
                />
              </Form.Item>
            )}

            {!hiddenFields?.notes && (
              <Form.Item>
                <Form.TextArea
                  name="notes"
                  aria-label="Campo per specificare le note"
                  label="Note"
                  rows={6}
                  disabled={disabledFields?.notes}
                />
              </Form.Item>
            )}
          </Form.Fieldset>
        );
      }}
    </Form>
  );
};

export default InvoiceForm;
